import classNames from "classnames";
import { FC, RefObject, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MultiValue, SingleValue } from "react-select";

import styles from "./DashBoardHeader.module.scss";

import { ReactComponent as Arrow } from "../../../../../assets/icons/arrow-icon.svg";
import { ReactComponent as CheckMark } from "../../../../../assets/icons/checkmark-icon.svg";
import { ReactComponent as Download } from "../../../../../assets/icons/down-icon.svg";
import { ReactComponent as Share } from "../../../../../assets/icons/share-icon.svg";
import { useClickOutside } from "../../../../../hooks/useClickOutside";
import { ISyncLogVersions } from "../../../../../types/dashBoard";
import { formatDate, formatTimeAsUtc } from "../../../../../utils/formatDate";
import { CustomButton } from "../../../../FormFields/CustomButton";
import { CustomSelect } from "../../../../FormFields/CustomSelect";
import { IOptions } from "../../../../FormFields/CustomSelect/types";
import { ReactPortal } from "../../../../ReactPortal";
import { ReactPortalSubstrate } from "../../../../ReactPortalSubstrate";
import { ReportDownload } from "../ReportDownload";
import { ShareReportModal } from "../SharePeportModal";

interface IProps {
  layoutRef: RefObject<HTMLDivElement>;
  option: ISyncLogVersions[];
}

export const DashBoardHeader: FC<IProps> = ({ layoutRef, option }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDownMenuOpen, setIsDownMenuOpen] = useState(false);

  const { reportId, syncLogId } = useParams();

  const containerRef = useRef<HTMLDivElement>(null);
  const shareRef = useRef<HTMLDivElement>(null);
  const downloadBtnRef = useRef<HTMLButtonElement>(null);
  const downloadRef = useRef<HTMLDivElement>(null);

  const onShareClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const onShareClose = () => {
    setIsMenuOpen(false);
  };

  const onDownloadClick = () => {
    setIsDownMenuOpen(!isDownMenuOpen);
  };

  const onDownloadClose = () => {
    setIsDownMenuOpen(false);
  };

  useClickOutside(shareRef, onShareClose);
  useClickOutside([downloadRef, downloadBtnRef], onDownloadClose);

  const onSelectChange = (
    newValue: MultiValue<IOptions> | SingleValue<IOptions>
  ) => {
    if (newValue) {
      const selectedOption = newValue as IOptions;
      if (selectedOption.id) {
        navigate(`/reports/${reportId}/${selectedOption.value}`);
      }
    }
  };

  const options = option.map((item) => ({
    value: item.syncLogId,
    label:
      formatDate(item.uploadedOn) +
      " " +
      formatTimeAsUtc(item.uploadedOn) +
      " " +
      "(UTC)",
    id: item.syncLogId,
    icon: <CheckMark />,
  }));

  return (
    <div className={styles.root}>
      <div className={styles.wrapperSelect}>
        <CustomSelect
          label="Report versions"
          placeholder="Select versions"
          selectTheme={"withoutBorder"}
          value={options.find((item) => item.value === syncLogId)}
          onChange={onSelectChange}
          options={options}
        />
      </div>
      <div className={styles.wrapper} ref={containerRef}>
        <CustomButton
          theme={"unstyled"}
          onClick={onShareClick}
          className={styles.shareBtn}
        >
          <Share /> Share
        </CustomButton>
        <CustomButton
          ref={downloadBtnRef}
          theme={"unstyled"}
          onClick={onDownloadClick}
          className={styles.shareBtn}
        >
          <Download /> Download{" "}
          <Arrow
            className={classNames(styles.arrow, {
              [styles.openMenu]: isDownMenuOpen,
            })}
          />
        </CustomButton>
      </div>

      {isDownMenuOpen && (
        <ReactPortal container={containerRef}>
          <ReportDownload
            ref={downloadRef}
            layoutRef={layoutRef}
            onCloseClick={onDownloadClose}
          />
        </ReactPortal>
      )}

      {isMenuOpen && (
        <ReactPortal>
          <ReactPortalSubstrate className={styles.portalWrapper}>
            <ShareReportModal
              ref={shareRef}
              onClose={onShareClose}
              layoutRef={layoutRef}
            />
          </ReactPortalSubstrate>
        </ReactPortal>
      )}
    </div>
  );
};
